import { useIntl } from 'react-intl'
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil'

import {
  Chip,
  ChipIconInfo,
  ChipIconWrapper,
  Divider,
  DIVIDER_VARIANT_DEFAULT,
  Flex,
  FlexItem,
  Grid,
  GridCell,
  Link,
  LINK_VARIANT_EXTRALIGHT,
  Panel,
  SPACING_24,
  SpacingVertical,
  Switcher,
  Text,
  TEXT_VARIANT_BOLD,
  Title,
  TITLE_VARIANT_M
} from 'mailup-web-components'

import { changeWizardStepCallback } from 'Pages/Campaign/recoil/callbacks/callbacks'
import { campaignState } from 'Pages/Campaign/recoil/state/campaign'
import { modalFilterDetailsState } from 'Pages/Campaign/recoil/state/modals'
import {
  checkFrequencyState,
  messageIsOnSentState,
  sendingRowData
} from 'Pages/Campaign/recoil/state/page'

import {
  arrDaysOfWeek,
  MATCH_FILTER_CONDITION_ALL,
  MATCH_FILTER_CONDITION_AT_LEAST_ONE
} from 'Constants/index'

const PanelSending = () => {
  const intl = useIntl()

  const changeWizardStep = useRecoilCallback(changeWizardStepCallback, [])
  const { isOnSent } = useRecoilValue(messageIsOnSentState)

  const onClickEdit = () => {
    changeWizardStep(3)
  }

  return (
    <Panel>
      <Flex justifyContent='space-between'>
        <FlexItem>
          <Title variant={TITLE_VARIANT_M}>
            {intl.formatMessage({
              id: 'Load.ContentsCampaignWizard.Step3TabEmail'
            })}
          </Title>
        </FlexItem>
        <FlexItem>
          <Link
            variant={LINK_VARIANT_EXTRALIGHT}
            onClick={onClickEdit}
            disabled={isOnSent}
            data-mu-test='campaign-wizard__step-4__edit-step-3-button'
          >
            {intl.formatMessage({ id: 'Load.MailupCommon.Edit' })}
          </Link>
        </FlexItem>
      </Flex>
      <SpacingVertical variant={SPACING_24} />
      <Flex flexDirection='column' rowGap='16px'>
        <SenderRow />
      </Flex>
      <SpacingVertical variant={SPACING_24} />
      <Divider variant={DIVIDER_VARIANT_DEFAULT} />
      <SpacingVertical variant={SPACING_24} />

      <Grid cols={12}>
        <GridCell span={4}>
          <Text variant={TEXT_VARIANT_BOLD}>
            {intl.formatMessage({
              id: 'Load.ContentsCampaignWizard.RecipientsSelectedTitle'
            })}
          </Text>
        </GridCell>
        <GridCell span={8}>
          <Flex flexDirection='column' rowGap='8px'>
            <SendingRecipientsColumn />
          </Flex>
        </GridCell>
      </Grid>

      <SpacingVertical variant={SPACING_24} />
      <Divider variant={DIVIDER_VARIANT_DEFAULT} />
      <SpacingVertical variant={SPACING_24} />
      <Grid cols={12}>
        <GridCell span={4}>
          <Text variant={TEXT_VARIANT_BOLD}>
            {intl.formatMessage({
              id: 'Load.ContentsCampaignWizard.FrequencyTitle'
            })}
          </Text>
        </GridCell>
        <GridCell span={8}>
          <Flex flexDirection='column' rowGap='8px'>
            <SendingFrequencyRightColumn />
          </Flex>
        </GridCell>
      </Grid>
    </Panel>
  )
}

export default PanelSending

const SenderRow = () => {
  const intl = useIntl()

  const {
    EmailOptions: { ReplyTo, SenderEmail, SenderName }
  } = useRecoilValue(campaignState)

  return (
    <>
      <Grid cols={12}>
        <GridCell span={4}>
          <Text variant={TEXT_VARIANT_BOLD}>
            {intl.formatMessage({
              id: 'Load.Common.SenderEmailLabel'
            })}
          </Text>
        </GridCell>
        <GridCell span={8}>
          <Text>{SenderEmail}</Text>
        </GridCell>
      </Grid>

      <Grid cols={12}>
        <GridCell span={4}>
          <Text variant={TEXT_VARIANT_BOLD}>
            {intl.formatMessage({
              id: 'Load.Common.SenderNameLabel'
            })}
          </Text>
        </GridCell>
        <GridCell span={8}>
          <Text>{SenderName}</Text>
        </GridCell>
      </Grid>

      <Grid cols={12}>
        <GridCell span={4}>
          <Text variant={TEXT_VARIANT_BOLD}>
            {intl.formatMessage({
              id: 'Load.Common.ReplyToEmailLabel'
            })}
          </Text>
        </GridCell>
        <GridCell span={8}>
          <Text>{ReplyTo}</Text>
        </GridCell>
      </Grid>
    </>
  )
}

const SendingRecipientsColumn = () => {
  const intl = useIntl()

  const { includeGroups, excludeGroups, applyFilters } =
    useRecoilValue(sendingRowData)
  const {
    EmailOptions: { ApplyFilterCondition }
  } = useRecoilValue(campaignState)

  const setModalFilterDetails = useSetRecoilState(modalFilterDetailsState)

  const onClickFilterInfo = ({ type, name, description, id }) => {
    setModalFilterDetails({
      show: true,
      type,
      name,
      description,
      id
    })
  }

  if (
    (!includeGroups && !excludeGroups && !applyFilters) ||
    (includeGroups.length === 0 &&
      excludeGroups.length === 0 &&
      applyFilters.length === 0)
  )
    return (
      <Text>
        {intl.formatMessage({
          id: 'Load.ContentsCampaignWizard.RecipientsSelectedAll'
        })}
      </Text>
    )

  return (
    <>
      {includeGroups.length > 0 && (
        <Flex flexDirection='column' rowGap='8px' margin='0 0 16px 0'>
          <Text>
            {intl.formatMessage({
              id: 'Load.SearchRecipients.IncludeGroups'
            })}
            :
          </Text>
          <FlexItem>
            <Flex flexWrap='wrap' rowGap='8px'>
              {includeGroups.map((label, index) => (
                <FlexItem key={'chip__group' + index} margin='0 8px 0 0'>
                  <Chip label={label} />
                </FlexItem>
              ))}
            </Flex>
          </FlexItem>
        </Flex>
      )}

      {excludeGroups.length > 0 && (
        <Flex flexDirection='column' rowGap='8px' margin='0 0 16px 0'>
          <>
            <Text>
              {intl.formatMessage({
                id: 'Load.SearchRecipients.ExcludeGroups'
              })}
              :
            </Text>
          </>
          <FlexItem>
            <Flex flexWrap='wrap' rowGap='8px'>
              {excludeGroups.map((label, index) => (
                <FlexItem key={'chip__group' + index} margin='0 8px 0 0'>
                  <Chip label={label} key={'chip__group' + index} />
                </FlexItem>
              ))}
            </Flex>
          </FlexItem>
        </Flex>
      )}

      {applyFilters.length > 0 && (
        <Flex flexDirection='column' rowGap='8px'>
          <FlexItem>
            <Flex alignItems='baseline'>
              <Text>
                {intl.formatMessage({
                  id: 'Load.SearchRecipients.ApplyFilters'
                })}
              </Text>
              {applyFilters.length > 1 && (
                <Switcher
                  value={
                    ApplyFilterCondition === true
                      ? MATCH_FILTER_CONDITION_AT_LEAST_ONE
                      : MATCH_FILTER_CONDITION_ALL
                  }
                  options={[
                    ApplyFilterCondition
                      ? {
                          label: intl.formatMessage({
                            id: 'Load.SearchRecipients.filterOne'
                          }),
                          value: MATCH_FILTER_CONDITION_AT_LEAST_ONE,
                          htmlAttrs: {
                            'data-tip': intl.formatMessage({
                              id: 'Load.SearchRecipients.filterOneTooltip'
                            })
                          }
                        }
                      : {
                          label: intl.formatMessage({
                            id: 'Load.SearchRecipients.filterAll'
                          }),
                          value: MATCH_FILTER_CONDITION_ALL,
                          htmlAttrs: {
                            'data-tip': intl.formatMessage({
                              id: 'Load.SearchRecipients.filterAllTooltip'
                            })
                          }
                        }
                  ]}
                />
              )}
            </Flex>
          </FlexItem>
          <FlexItem>
            <Flex flexWrap='wrap' rowGap='8px'>
              {applyFilters.map((elem, index) => (
                <FlexItem key={'chip__filter' + index} margin='0 8px 0 0'>
                  <Chip
                    label={`ID ${elem.id} - ${elem.name}`}
                    key={'chip__group' + index}
                  >
                    <ChipIconWrapper
                      onClick={() => onClickFilterInfo(elem)}
                      data-mu-test='campaign-wizard__step-4__apply-filter-info'
                    >
                      <ChipIconInfo />
                    </ChipIconWrapper>
                  </Chip>
                </FlexItem>
              ))}
            </Flex>
          </FlexItem>
        </Flex>
      )}
    </>
  )
}

const SendingFrequencyRightColumn = () => {
  const intl = useIntl()

  const { CheckFrequencyType, CheckNumberOfNewContents, Timezone } =
    useRecoilValue(campaignState)
  const { h, m, d } = useRecoilValue(checkFrequencyState)

  const getDays = () => {
    let daysString = ' '

    arrDaysOfWeek.forEach((day) => {
      if (d.includes(day.value)) {
        daysString += day.name + ', '
      }
    })

    daysString = daysString.endsWith(', ')
      ? daysString.substring(0, daysString.length - 2)
      : daysString

    return daysString
  }

  if (CheckFrequencyType === 1) {
    return (
      <Text>
        {intl.formatMessage(
          {
            id: 'Load.ContentsCampaignWizard.FrequencyRecap'
          },
          {
            N: CheckNumberOfNewContents
          }
        )}
      </Text>
    )
  }

  if (CheckFrequencyType === 2) {
    return (
      <>
        <Text>
          {intl.formatMessage(
            {
              id: 'Load.ContentsCampaignWizard.FrequencyRecapAlmeno'
            },
            {
              N: CheckNumberOfNewContents
            }
          )}
        </Text>
        <Text>
          {intl.formatMessage(
            {
              id: 'Load.ContentsCampaignWizard.FrequencyCron'
            },
            {
              DAYS: getDays(),
              H: h.toString().length === 1 ? `0${h}` : h,
              M: m.toString().length === 1 ? `0${m}` : m,
              TZ: Timezone
            }
          )}
        </Text>
      </>
    )
  }
}
